import React, { useState, useEffect } from 'react';
import { signIn, confirmSignIn, signOut } from 'aws-amplify/auth';
import { publicApiCall, isMFASetupPending } from '../services/api';
import toast from 'react-hot-toast';
import MFAVerificationModal from './MFAVerificationModal';
import MFASetupModal from './MFASetupModal';

interface LoginProps {
  onLogin: (user: any) => void;
  sessionExpiredMessage?: string | null;
}

const Login: React.FC<LoginProps> = ({ onLogin, sessionExpiredMessage }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetCodeForm, setShowResetCodeForm] = useState(false);
  const [showMFAVerification, setShowMFAVerification] = useState(false);
  const [showMFASetup, setShowMFASetup] = useState(false);

  // Admin-configured strength rules, fetched from the public endpoint. Defaults match
  // the backend so the forms work even if the request fails - Cognito enforces the real
  // policy server-side either way.
  const [passwordPolicy, setPasswordPolicy] = useState({
    minimumLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSymbols: true
  });

  useEffect(() => {
    publicApiCall('/auth/password-policy')
      .then((policy) => setPasswordPolicy((current) => ({ ...current, ...policy })))
      .catch((error) => console.error('Using default password rules:', error));
  }, []);

  // Returns an error message, or null when the password satisfies the policy
  const validatePassword = (value: string): string | null => {
    if (value.length < passwordPolicy.minimumLength) {
      return `Password must be at least ${passwordPolicy.minimumLength} characters long`;
    }
    if (passwordPolicy.requireUppercase && !/[A-Z]/.test(value)) {
      return 'Password must include an uppercase letter';
    }
    if (passwordPolicy.requireLowercase && !/[a-z]/.test(value)) {
      return 'Password must include a lowercase letter';
    }
    if (passwordPolicy.requireNumbers && !/[0-9]/.test(value)) {
      return 'Password must include a number';
    }
    if (passwordPolicy.requireSymbols && !/[^A-Za-z0-9]/.test(value)) {
      return 'Password must include a symbol';
    }
    return null;
  };

  // Show session expired message as toast
  useEffect(() => {
    if (sessionExpiredMessage) {
      toast.error(sessionExpiredMessage, {
        duration: 5000,
        icon: '🔒',
      });
    }
  }, [sessionExpiredMessage]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await signIn({ username: email, password });
      
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNeedsPasswordChange(true);
        toast('Please set a new password to continue');
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setShowMFAVerification(true);
        toast('Please enter your MFA code');
      } else if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        // First time MFA setup after admin enabled it - show QR code
        setShowMFASetup(true);
        toast('Please set up two-factor authentication');
      } else {
        // Login successful - hold the user here if MFA is required but not enrolled
        if (await isMFASetupPending()) {
          setShowMFASetup(true);
          toast('Your administrator requires you to set up two-factor authentication');
          return;
        }

        toast.success('Login successful!');
        onLogin(result);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMFASuccess = (result: any) => {
    setShowMFAVerification(false);
    toast.success('Login successful!');
    onLogin(result);
  };

  const handleMFACancel = () => {
    setShowMFAVerification(false);
    toast.error('Login cancelled');
  };

  const handleMFASetupSuccess = (result: any) => {
    setShowMFASetup(false);
    toast.success('MFA setup complete! Login successful!');
    onLogin(result);
  };

  const handleMFASetupCancel = async () => {
    // signIn() has already banked tokens in storage by this point, so cancelling
    // must discard the session. Otherwise the user could simply reload the page and
    // App.tsx would restore them straight into the portal without enrolling.
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out after cancelled MFA setup:', error);
    }
    setShowMFASetup(false);
    setPassword('');
    toast.error('MFA setup is required to sign in. Please log in again to complete setup.');
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    const policyError = validatePassword(newPassword);
    if (policyError) {
      toast.error(policyError);
      return;
    }

    try {
      setLoading(true);
      
      // First, use Amplify to confirm the password change
      const result = await confirmSignIn({
        challengeResponse: newPassword
      });
      
      // Then call our backend to ensure the status is cleared in DynamoDB
      try {
        await publicApiCall('/auth/clear-password-reset-status', {
          method: 'POST',
          body: JSON.stringify({ email })
        });
      } catch (statusError) {
        console.warn('Failed to clear password reset status:', statusError);
        // Don't fail the login for this - it's not critical
      }

      // New users land here on first login (FORCE_CHANGE_PASSWORD), and MFA is
      // required at onboarding - so this path has to run the same gate as
      // handleLogin, otherwise onboarding would skip enrolment entirely.
      if (await isMFASetupPending()) {
        setNeedsPasswordChange(false);
        setShowMFASetup(true);
        toast.success('Password updated. Now set up two-factor authentication to continue.');
        return;
      }

      toast.success('Password updated successfully!');
      onLogin(result);
    } catch (error: any) {
      console.error('Password change error:', error);
      toast.error(error.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      await publicApiCall('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email })
      });
      toast.success('Password reset code sent to your email');
      setShowForgotPassword(false);
      setShowResetCodeForm(true);
    } catch (error: any) {
      console.error('Forgot password error:', error);
      toast.error(error.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordWithCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetCode || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    const policyError = validatePassword(newPassword);
    if (policyError) {
      toast.error(policyError);
      return;
    }

    try {
      setLoading(true);
      await publicApiCall('/auth/confirm-forgot-password', {
        method: 'POST',
        body: JSON.stringify({
          email,
          code: resetCode,
          newPassword
        })
      });
      
      toast.success('Password reset successfully! Please sign in with your new password.');
      setShowResetCodeForm(false);
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Reset password error:', error);
      toast.error(error.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (showMFASetup) {
    return (
      <MFASetupModal
        onSuccess={handleMFASetupSuccess}
        onCancel={handleMFASetupCancel}
      />
    );
  }

  if (showMFAVerification) {
    return (
      <MFAVerificationModal
        onSuccess={handleMFASuccess}
        onCancel={handleMFACancel}
      />
    );
  }

  if (needsPasswordChange) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">Set New Password</h2>
              <p className="mt-2 text-sm text-gray-600">
                Please set a new password for your account
              </p>
            </div>

            <form onSubmit={handlePasswordChange} className="space-y-6">
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter new password"
                  required
                  minLength={8}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Must be at least 8 characters long
                </p>
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Confirm new password"
                  required
                  minLength={8}
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">Reset Password</h2>
              <p className="mt-2 text-sm text-gray-600">
                Enter your email to receive a password reset code
              </p>
            </div>

            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Back to Login
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {loading ? 'Sending...' : 'Send Code'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (showResetCodeForm) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">Enter Reset Code</h2>
              <p className="mt-2 text-sm text-gray-600">
                Enter the code sent to {email} and your new password
              </p>
            </div>

            <form onSubmit={handleResetPasswordWithCode} className="space-y-6">
              <div>
                <label htmlFor="reset-code" className="block text-sm font-medium text-gray-700">
                  Reset Code
                </label>
                <input
                  id="reset-code"
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter 6-digit code"
                  required
                />
              </div>

              <div>
                <label htmlFor="reset-new-password" className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <input
                  id="reset-new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter new password"
                  required
                  minLength={8}
                />
              </div>

              <div>
                <label htmlFor="reset-confirm-password" className="block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <input
                  id="reset-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Confirm new password"
                  required
                  minLength={8}
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetCodeForm(false);
                    setShowForgotPassword(true);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">S3 Portal</h2>
            <p className="mt-2 text-sm text-gray-600">
              Sign in to your account
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your password"
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Forgot your password?
            </button>
            <p className="mt-2 text-xs text-gray-500">
              If you received a password reset email, use your temporary password to sign in.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;